import type { ClientFormData } from './schema';

/**
 * Assigns non-empty company identity fields to the payload.
 */
function assignCompanyFields(result: Record<string, unknown>, identity: ClientFormData['identity']): void {
  if (identity.cnpj) result.cnpj = identity.cnpj;
  if (identity.corporateName) result.corporate_name = identity.corporateName;
  if (identity.tradeName) result.trade_name = identity.tradeName;
}

/**
 * Assigns non-empty individual identity fields to the payload.
 */
function assignIndividualFields(result: Record<string, unknown>, identity: ClientFormData['identity']): void {
  if (identity.cpf) result.cpf = identity.cpf;
  if (identity.rg) result.rg = identity.rg;
  if (identity.issuingAgency) result.issuing_agency = identity.issuingAgency;
}

/**
 * Builds identity payload section from form data.
 */
function buildIdentityPayload(identity: ClientFormData['identity']): Record<string, unknown> {
  const result: Record<string, unknown> = { person_type: identity.personType };
  if (identity.personType === 'company') {
    assignCompanyFields(result, identity);
  } else {
    assignIndividualFields(result, identity);
  }
  return result;
}

/**
 * Builds personal info payload section from form data.
 */
function buildPersonalInfoPayload(info: ClientFormData['personalInfo']): Record<string, unknown> {
  const result: Record<string, unknown> = { full_name: info.fullName.trim() };
  if (info.birthDate) result.birth_date = info.birthDate;
  if (info.profession) result.profession = info.profession;
  if (info.maritalStatus) result.marital_status = info.maritalStatus;
  if (info.nationality) result.nationality = info.nationality;
  return result;
}

/**
 * Converts a raw string value to a number, optionally stripping non-digits.
 */
function toNumeric(val: string, stripNonDigit: boolean): number {
  return Number(stripNonDigit ? val.replace(/\D/g, '') : val);
}

/**
 * Assigns array fields, empty ones included.
 *
 * An omitted key is not an erased key: ``update_by_id`` merges the incoming
 * demand_profile into the stored one field by field, so a chip group the
 * broker just cleared has to travel as ``[]`` or last week's selection
 * survives the save and the form silently disagrees with the record.
 */
function assignArrayFields(
  profile: Record<string, unknown>,
  fields: Array<[string, string[] | undefined]>,
): void {
  for (const [key, arr] of fields) {
    profile[key] = arr ?? [];
  }
}

/**
 * Assigns numeric fields, sending ``null`` for the ones cleared to blank.
 *
 * Same merge semantics as the array groups above: blank has to be written,
 * not omitted. ``DemandProfile.from_dict`` reads a stored ``null`` back as
 * "not set", so the round-trip stays faithful.
 */
function assignClearableNumerics(
  profile: Record<string, unknown>,
  fields: Array<[string, string | undefined, boolean]>,
): void {
  for (const [key, val, strip] of fields) {
    profile[key] = val ? toNumeric(val, strip) : null;
  }
}

/**
 * Builds demand profile payload when present.
 */
function buildDemandProfilePayload(dp: NonNullable<ClientFormData['demandProfile']>): Record<string, unknown> | null {
  const profile: Record<string, unknown> = {
    business_type: dp.businessType || null,
    furnished: dp.furnished || null,
    pet_friendly: dp.petFriendly ? dp.petFriendly === 'yes' : null,
  };
  assignClearableNumerics(profile, [
    ['price_min', dp.priceMin, true],
    ['price_max', dp.priceMax, true],
    ['bedrooms_min', dp.bedroomsMin, false],
    ['area_min', dp.areaMin, false],
    ['bathrooms_min', dp.bathroomsMin, false],
    ['parking_min', dp.parkingMin, false],
    ['suites_min', dp.suitesMin, false],
  ]);
  assignArrayFields(profile, [
    ['property_kinds', dp.propertyKinds],
    ['cities', dp.cities],
    ['neighborhoods', dp.neighborhoods],
    ['amenities_internal', dp.amenitiesInternal],
    ['amenities_external', dp.amenitiesExternal],
    ['amenities_security', dp.amenitiesSecurity],
    ['condo_leisure', dp.condoLeisure],
    ['condo_security', dp.condoSecurity],
    ['tags', dp.tags],
  ]);
  return profile;
}

/**
 * Builds address payload when present.
 */
function buildAddressPayload(address: NonNullable<ClientFormData['address']>): Record<string, unknown> | null {
  const fieldMap: Array<[string, string | undefined]> = [
    ['zip_code', address.zipCode],
    ['street', address.street],
    ['number', address.number],
    ['complement', address.complement],
    ['neighborhood', address.neighborhood],
    ['city', address.city],
    ['state', address.state],
  ];
  const addr: Record<string, unknown> = {};
  for (const [key, val] of fieldMap) {
    if (val) addr[key] = val;
  }
  return Object.keys(addr).length > 0 ? addr : null;
}

/**
 * Builds financial payload when present.
 */
function buildFinancialPayload(financial: NonNullable<ClientFormData['financial']>): Record<string, unknown> | null {
  const fin: Record<string, unknown> = {};
  if (financial.monthlyIncome) fin.monthly_income = Number(financial.monthlyIncome.replace(/\D/g, ''));
  if (financial.incomeProof) fin.income_proof = financial.incomeProof;
  return Object.keys(fin).length > 0 ? fin : null;
}

/**
 * Assigns the result of a builder to the payload when non-null.
 */
function assignOptionalSection(
  payload: Record<string, unknown>,
  key: string,
  builder: () => Record<string, unknown> | null,
): void {
  const value = builder();
  if (value) payload[key] = value;
}

/**
 * Assigns the demand-profile, address and financial sections to the payload
 * when the form data has them.
 *
 * `roles` is deliberately absent: the backend derives the client's role from
 * the presence of demand_profile/supply_profile and rejects a top-level
 * `roles` key on PUT with a 422. The chips stay form-only state.
 *
 * @param payload - The payload being assembled.
 * @param data - The validated client form data.
 */
function assignOptionalPayloadSections(payload: Record<string, unknown>, data: ClientFormData): void {
  if (data.demandProfile) assignOptionalSection(payload, 'demand_profile', () => buildDemandProfilePayload(data.demandProfile!));
  if (data.address) assignOptionalSection(payload, 'address', () => buildAddressPayload(data.address!));
  if (data.financial) assignOptionalSection(payload, 'financial', () => buildFinancialPayload(data.financial!));
}

/**
 * Builds the `assignment` section, deciding what an empty broker means.
 *
 * On edit, `assignment` is shallow-merged server-side, so clearing a
 * previously assigned broker has to travel as an explicit value — an omitted
 * key would leave last week's name in place. That value is an empty string,
 * NOT `null`: the API declares `primary_broker` as `nullable: true`, but the
 * server's OpenAPI validator is Draft-7 and does not honor `nullable`, so a
 * `null` is rejected with "Your data has 1 error" (`None is not of type
 * 'string'`) — the save fails for every client with no broker assigned. An
 * empty string overwrites the stored value just the same, clears the owner
 * (the backend treats a falsy `primary_broker` as "nobody"), and validates as
 * a string. On create there is nothing to clear, so the key is simply omitted.
 */
function buildAssignmentPayload(data: ClientFormData, mode: 'create' | 'edit'): Record<string, unknown> {
  const contacts = data.contacts
    .filter((c) => c.value.trim())
    .map((c) => ({ type: c.type, value: c.value.trim(), ...(c.label ? { label: c.label } : {}) }));
  const trimmedBroker = data.primaryBroker?.trim();
  const assignment: Record<string, unknown> = { contacts };
  if (trimmedBroker) {
    assignment.primary_broker = trimmedBroker;
  } else if (mode === 'edit') {
    assignment.primary_broker = '';
  }
  return assignment;
}

/** What the record held when the edit form opened, for clearing. */
export type StoredClientValues = Partial<ClientFormData>;

/**
 * Assigns text fields that a blank value has to clear on edit.
 *
 * Every block of the client is shallow-merged on update, so an omitted key
 * keeps its stored value. A filled value is always sent; a blank one only
 * when the record held something, as `""` (the request validator refuses
 * `null`, and the backend reads an empty enum as cleared). A field that was
 * never filled is left out, so saving does not add empty keys to the record.
 *
 * @param target - The payload block being assembled.
 * @param fields - `[api key, form value, stored value]` triples.
 */
function assignClearableText(
  target: Record<string, unknown>,
  fields: Array<[string, string | undefined, string | undefined]>,
): void {
  for (const [key, value, stored] of fields) {
    const trimmed = value?.trim() ?? '';
    if (trimmed || stored?.trim()) target[key] = trimmed;
  }
}

/**
 * Adds the personal attributes of plan 2026-10-28 (§3.2, §11.3) to the
 * personal_info block.
 *
 * @param info - The personal_info block being assembled.
 * @param data - The form's personal info.
 * @param stored - The personal info the record held, on edit.
 */
function assignPersonalAttributes(
  info: Record<string, unknown>,
  data: ClientFormData['personalInfo'],
  stored: Partial<ClientFormData['personalInfo']> = {},
): void {
  assignClearableText(info, [
    ['gender', data.gender, stored.gender],
    ['education', data.education, stored.education],
    ['job_title', data.jobTitle, stored.jobTitle],
    ['employer', data.employer, stored.employer],
    ['industry', data.industry, stored.industry],
  ]);
}

/**
 * Adds `client_roles` to the demand_profile block.
 *
 * Sent whole whenever the client has roles or had them (an empty list is a
 * deliberate "no roles" and clears them). The backend writes the legacy
 * `client_role` as its projection, so the two never disagree.
 *
 * @param payload - The payload being assembled.
 * @param roles - The roles picked in the form.
 * @param storedRoles - The effective roles the record held, on edit.
 */
function assignClientRoles(
  payload: Record<string, unknown>,
  roles: string[] | undefined,
  storedRoles: string[] | undefined,
): void {
  const list = roles ?? [];
  if (list.length === 0 && !storedRoles?.length) return;
  const profile = (payload.demand_profile as Record<string, unknown> | undefined) ?? {};
  payload.demand_profile = { ...profile, client_roles: list };
}

/**
 * Builds the origin of a new client. Origin is write-once: only a create
 * sends it, and a client with none is recorded as manual by the backend.
 *
 * @param origin - The form's origin fields.
 * @returns The origin block, or null when nothing was filled.
 */
function buildOriginPayload(origin: ClientFormData['origin']): Record<string, unknown> | null {
  if (!origin) return null;
  const result: Record<string, unknown> = {};
  assignClearableText(result, [
    ['source_detail', origin.sourceDetail, undefined],
    ['campaign', origin.campaign, undefined],
  ]);
  if (!origin.channel && Object.keys(result).length === 0) return null;
  return { channel: origin.channel || 'manual', ...result };
}

/**
 * Adds the fields of plan 2026-10-28 that live outside personal_info:
 * roles, who captured the client, the previous system's code and, on
 * create, the origin.
 *
 * @param payload - The payload being assembled.
 * @param data - The validated client form data.
 * @param mode - Create or edit.
 * @param stored - What the record held, on edit.
 */
function assignAttributeSections(
  payload: Record<string, unknown>,
  data: ClientFormData,
  mode: 'create' | 'edit',
  stored: StoredClientValues,
): void {
  assignClientRoles(payload, data.clientRoles, stored.clientRoles);
  assignClearableText(payload.assignment as Record<string, unknown>, [
    ['captured_by', data.capturedBy, stored.capturedBy],
  ]);
  const metadata: Record<string, unknown> = {};
  assignClearableText(metadata, [['legacy_code', data.legacyCode, stored.legacyCode]]);
  if (Object.keys(metadata).length > 0) payload.metadata = metadata;
  if (mode === 'create') assignOptionalSection(payload, 'origin', () => buildOriginPayload(data.origin));
}

/**
 * Builds the nested API payload from validated form data.
 *
 * @param data - The validated client form data.
 * @param mode - Whether this payload is for creating or editing a client.
 * @param stored - On edit, the values the record held when the form
 *   opened; a field blanked by the broker is sent as a clear only when
 *   there was something to clear.
 * @returns The nested API payload.
 */
export function buildPayloadFromFormData(
  data: ClientFormData,
  mode: 'create' | 'edit' = 'create',
  stored: StoredClientValues = {},
): Record<string, unknown> {
  const personalInfo = buildPersonalInfoPayload(data.personalInfo);
  assignPersonalAttributes(personalInfo, data.personalInfo, stored.personalInfo);
  const payload: Record<string, unknown> = {
    identity: buildIdentityPayload(data.identity),
    personal_info: personalInfo,
    assignment: buildAssignmentPayload(data, mode),
  };

  assignOptionalPayloadSections(payload, data);
  assignAttributeSections(payload, data, mode, stored);

  return payload;
}
